import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, POINT_SPRITE_GLSL } from '../shaderChunks'

/**
 * Fireworks: a few big shells rise and burst. Each burst opens into arms, and
 * every arm tip then SUBDIVIDES into a cluster of secondary sparks (two-stage
 * explosion) that arc out and fall under gravity. Kept calm by default — the
 * music only nudges the burst size; turn Reactivity up for a wilder show.
 */
export class Fireworks extends BasePrimitive {
  object3D: THREE.Points
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute vec3 aLaunch;    // launchX, burstY, launchZ (per shell)
        attribute vec3 aArmDir;    // primary explosion direction (per arm)
        attribute vec3 aSubDir;    // secondary direction after the split (per spark)
        attribute float aPhase;    // shell lifecycle offset (per shell)
        attribute float aSeed;
        uniform float uTime, uBass, uBeat, uLevel, uReactivity, uBassPunch, uPixelRatio, uPointScale, uHue;
        varying float vLife;
        varying float vHue;
        void main() {
          // Steady cadence + steady size, INDEPENDENT of the music, so loud parts
          // never speed it up or blow the bursts into a cloud. The music only
          // adds a subtle brightness shimmer (in the fragment).
          float speed = 0.06;
          float p = fract(aPhase + uTime * speed);
          float pr = 0.18;                       // fraction spent rising
          float pEnd = 0.62;                     // burst fully faded -> dark gap before relaunch
          float burstR = 4.4;
          float subR = 1.7;
          float pSplit = 0.45;                   // when each arm subdivides
          vec3 pos;
          float life;
          if (p < pr) {
            float t = p / pr;
            pos = vec3(aLaunch.x, mix(-7.0, aLaunch.y, t), aLaunch.z);
            life = 0.12;                         // dim rising trail
          } else if (p < pEnd) {
            float tb = (p - pr) / (pEnd - pr);   // 0..1 across the burst window
            vec3 burstPoint = aLaunch;
            if (tb < pSplit) {
              // Stage 1: the arms shoot out from the burst point.
              pos = burstPoint + aArmDir * (tb / pSplit) * burstR;
            } else {
              // Stage 2: each arm tip subdivides into secondary sparks.
              vec3 splitPoint = burstPoint + aArmDir * burstR;
              float ts = (tb - pSplit) / (1.0 - pSplit);
              pos = splitPoint + aSubDir * ts * subR;
            }
            pos.y -= tb * tb * 3.4;              // gravity
            life = (1.0 - tb) * (1.0 - tb);      // fade to 0 by pEnd
          } else {
            // Dark gap: the shell is gone until it relaunches.
            pos = vec3(0.0, -100.0, 0.0);
            life = 0.0;
          }
          vLife = life;
          vHue = fract(uHue + aPhase * 1.7 + aSeed * 0.06);
          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mv;
          float px = 2.6 + life * 6.5;
          gl_PointSize = clamp(px * (6.0 / -mv.z), 1.0, 26.0) * uPixelRatio * uPointScale;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uBeat, uReactivity;
        varying float vLife;
        varying float vHue;
        ${hsv2rgb}
        ${POINT_SPRITE_GLSL}
        void main() {
          float core = pointMask();
          if (core < 0.01) discard;
          vec3 col = hsv2rgb(vec3(vHue, 0.8, 0.6 + vLife * 0.4));
          col *= 1.0 + uBeat * 0.3 * uReactivity;   // subtle beat shimmer
          float a = core * (uPointSolid > 0.5 ? clamp(vLife + 0.25, 0.0, 1.0) : vLife * 0.95);
          if (a < 0.01) discard;
          gl_FragColor = vec4(col, a);
        }
      `,
    })
    this.object3D = new THREE.Points(new THREE.BufferGeometry(), this.material)
    this.object3D.frustumCulled = false
    this.rebuild({ density: 1 } as VisualParams)
  }

  update(params: VisualParams): void {
    const solid = params.pointSolid === true
    this.material.blending = solid ? THREE.NormalBlending : THREE.AdditiveBlending
    this.material.depthWrite = solid
  }

  rebuild(params: VisualParams): void {
    const d = params.density ?? 1
    // Few, big shells; each has several arms; each arm subdivides into sparks.
    const shells = 14
    const arms = 16
    const subs = Math.max(4, Math.floor(10 * d))
    const count = shells * arms * subs
    const positions = new Float32Array(count * 3)
    const launch = new Float32Array(count * 3)
    const armDir = new Float32Array(count * 3)
    const subDir = new Float32Array(count * 3)
    const phase = new Float32Array(count)
    const seed = new Float32Array(count)
    let i = 0
    for (let s = 0; s < shells; s++) {
      const lx = (Math.random() * 2 - 1) * 7
      const by = 1.5 + Math.random() * 4
      const lz = (Math.random() * 2 - 1) * 4
      const ph = Math.random()
      for (let a = 0; a < arms; a++) {
        const ad = randUnit()
        for (let k = 0; k < subs; k++) {
          // Secondary direction: the arm direction scattered a bit.
          const sd = randUnit()
          const mx = ad[0] * 0.6 + sd[0] * 0.7
          const my = ad[1] * 0.6 + sd[1] * 0.7
          const mz = ad[2] * 0.6 + sd[2] * 0.7
          const ml = Math.hypot(mx, my, mz) || 1
          launch[i * 3] = lx
          launch[i * 3 + 1] = by
          launch[i * 3 + 2] = lz
          armDir[i * 3] = ad[0]
          armDir[i * 3 + 1] = ad[1]
          armDir[i * 3 + 2] = ad[2]
          subDir[i * 3] = mx / ml
          subDir[i * 3 + 1] = my / ml
          subDir[i * 3 + 2] = mz / ml
          phase[i] = ph
          seed[i] = Math.random()
          positions[i * 3] = lx
          positions[i * 3 + 1] = by
          positions[i * 3 + 2] = lz
          i++
        }
      }
    }
    const geo = this.object3D.geometry
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aLaunch', new THREE.BufferAttribute(launch, 3))
    geo.setAttribute('aArmDir', new THREE.BufferAttribute(armDir, 3))
    geo.setAttribute('aSubDir', new THREE.BufferAttribute(subDir, 3))
    geo.setAttribute('aPhase', new THREE.BufferAttribute(phase, 1))
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1))
    geo.computeBoundingSphere()
  }
}

/** Random unit vector (uniform on the sphere). */
function randUnit(): [number, number, number] {
  const z = Math.random() * 2 - 1
  const a = Math.random() * Math.PI * 2
  const r = Math.sqrt(1 - z * z)
  return [Math.cos(a) * r, z, Math.sin(a) * r]
}
